import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();
  const { setSession } = useAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    if (password !== confirm) return toast.error("Las contraseñas no coinciden");
    if (!token) return toast.error("Enlace inválido — pide uno nuevo");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
      // Auto-login
      if (data?.token && data?.user && setSession) {
        setSession(data.token, data.user);
      }
      setDone(true);
      setTimeout(() => nav("/app", { replace: true }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restablecer la contraseña");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] topo-bg px-6 py-12">
      <div className="w-full max-w-md bg-white border border-[#E2DCD0] p-10 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
          <span className="font-display text-2xl text-[#0A1128]">VoyageMarkt</span>
        </div>

        {!done ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Nueva contraseña</p>
            <h1 className="font-display text-3xl text-[#0A1128] leading-tight">Crea tu nueva contraseña.</h1>
            <p className="text-[#5C616A] mt-3 mb-8 text-sm">
              Mínimo 6 caracteres. Una vez guardada te llevamos directo al panel.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Nueva contraseña</Label>
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Confirmar contraseña</Label>
                <Input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
                />
              </div>
              <Button
                data-testid="reset-submit-button"
                type="submit"
                disabled={busy || !token}
                className="w-full bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm h-11"
              >
                {busy ? "Guardando..." : "Restablecer contraseña"}
              </Button>
              {!token && (
                <p className="text-xs text-[#D95D39] mt-2">
                  Falta el token de recuperación. Pide un nuevo enlace en el formulario.
                </p>
              )}
            </form>
          </>
        ) : (
          <div data-testid="reset-success-state">
            <CheckCircle className="w-10 h-10 text-[#2D6A4F] mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-[#0A1128] leading-tight">Listo.</h1>
            <p className="text-[#5C616A] mt-3 text-sm">
              Tu contraseña ha sido actualizada. Te estamos llevando al panel…
            </p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#E2DCD0]">
          <Link
            data-testid="back-to-login-link"
            to="/login"
            className="text-sm text-[#5C616A] hover:text-[#0A1128] inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver al login
          </Link>
        </div>
      </div>
    </div>
  );
}
